import {createElement, type ReactNode} from "react";
import type {RenderableDocument, DibElementChild} from "./types";

// --- Constants ---

/** HTML attributes renamed for React (RenderNodeFactory.tsx:305-311) */
const RENAMED_ATTRIBUTES: Record<string, string> = {
    class: "className",
    colspan: "colSpan",
    rowspan: "rowSpan",
    "xml:space": "xmlSpace",
};

/**
 * Attributes stripped from output (RenderNodeFactory.tsx:313-320).
 * Note: "style" is NOT in this set — it is handled separately by parseStyle
 * before the strip check runs (matching the original's parsedAttributes flow).
 */
const STRIPPED_ATTRIBUTES = new Set([
    "exId",
    "onclick",
    "serialnumber",
    "valign",
    "cType",
]);

/** HTML void elements that must not receive children (RenderNodeFactory.tsx:377-393) */
const VOID_TAGS = new Set([
    "area", "base", "br", "col", "command", "embed",
    "hr", "input", "keygen", "link", "meta", "param",
    "source", "track", "wbr",
]);

/**
 * Table-related tags exempt from style stripping — they keep ALL inline styles
 * because admins manually control table appearance (RenderNodeFactory.tsx:15-27).
 */
const STYLE_EXEMPT_TAGS = new Set([
    "table", "tbody", "col", "colgroup", "tr", "td", "th",
]);

// --- Style parsing ---

/**
 * Whitelisted CSS properties for non-exempt elements.
 * Maps CSS property name → React camelCase name.
 * (RenderNodeFactory.tsx:322-340)
 */
const ALLOWED_STYLES: Record<string, string> = {
    "list-style-type": "listStyleType",
    "text-align": "textAlign",
    "border-collapse": "borderCollapse",
};

type StyleObject = Record<string, string>;

/**
 * Parses an inline CSS string into a React style object.
 *
 * - Exempt elements (tables): all styles preserved, keys camelCased.
 * - Non-exempt elements: only whitelisted properties kept.
 * - Returns null if no applicable styles found.
 */
function parseStyle(styleStr: string, exempt: boolean): StyleObject | null {
    const styles: Record<string, string> = {};

    for (const part of styleStr.split(";")) {
        const colonIdx = part.indexOf(":");
        if (colonIdx === -1) continue;
        const key = part.slice(0, colonIdx).trim();
        const value = part.slice(colonIdx + 1).trim();
        if (key && value) {
            styles[key] = value;
        }
    }

    if (exempt) {
        const result: StyleObject = {};
        for (const [key, value] of Object.entries(styles)) {
            const camelKey = key.replace(/-([a-z])/g, (_, c: string) =>
                c.toUpperCase(),
            );
            result[camelKey] = value;
        }
        return Object.keys(result).length > 0 ? result : null;
    }

    const result: StyleObject = {};
    for (const [cssKey, camelKey] of Object.entries(ALLOWED_STYLES)) {
        if (styles[cssKey]) {
            result[camelKey] = styles[cssKey];
        }
    }
    return Object.keys(result).length > 0 ? result : null;
}

// --- Attribute processing ---

/**
 * Converts HTML attributes from the JSON into React-compatible props.
 *
 * Processing order (matches original RenderNodeFactory):
 * 1. Renamed attributes (class → className, etc.)
 * 2. Style parsing (before strip check — style is in strippedAttributes
 *    in the original but parsedAttributes runs first)
 * 3. Stripped attributes (exId, onclick, etc.)
 * 4. target="_blank" → adds rel="noopener noreferrer"
 * 5. Pass-through for everything else
 */
function fixAttributes(
    attrs: Record<string, string>,
    tagName: string,
): Record<string, unknown> {
    const exempt = STYLE_EXEMPT_TAGS.has(tagName);
    const result: Record<string, unknown> = {};

    for (const [key, value] of Object.entries(attrs)) {
        if (RENAMED_ATTRIBUTES[key]) {
            result[RENAMED_ATTRIBUTES[key]] = value;
            continue;
        }

        if (key === "style") {
            const parsed = parseStyle(value, exempt);
            if (parsed) {
                result.style = parsed;
            }
            continue;
        }

        if (STRIPPED_ATTRIBUTES.has(key)) {
            continue;
        }

        if (key === "target" && value === "_blank") {
            result.target = value;
            result.rel = "noopener noreferrer";
            continue;
        }

        result[key] = value;
    }

    return result;
}

// --- Rendering functions ---

/**
 * Renders a single element from the document's flat element map.
 * Looks up the element by ID, processes attributes, and recursively
 * renders children via createElement.
 *
 * Returns null (with console.warn) if the element is missing.
 */
export function renderNode(
    doc: RenderableDocument,
    id: string,
    key?: string,
): ReactNode {
    const element = doc.elements[id];

    if (!element) {
        console.warn(`renderNode: element "${id}" not found in document`);
        return null;
    }

    // Skip hidden elements (RenderNodeFactory.tsx:167-169)
    const className = element.attributes?.class ?? "";
    if (className.includes("dib-x-hide")) {
        return null;
    }

    const attrs = fixAttributes(element.attributes ?? {}, element.name);
    attrs.key = key ?? id;

    // Void tags render without children (RenderNodeFactory.tsx:259-264)
    if (VOID_TAGS.has(element.name.toLowerCase())) {
        return createElement(element.name, attrs);
    }

    const children = renderChildren(doc, element.children);
    return createElement(element.name, attrs, ...children);
}

/**
 * Maps a children array to ReactNode[].
 * Text children become strings, element references recurse via renderNode.
 */
export function renderChildren(
    doc: RenderableDocument,
    children: DibElementChild[],
): ReactNode[] {
    if (!children || children.length === 0) return [];

    return children.map((child) => {
        if ("text" in child) {
            return child.text;
        }
        return renderNode(doc, child.id);
    });
}

/**
 * Entry point: renders the document's root elements (the main content body).
 * Iterates doc.root and renders each entry.
 */
export function renderDocumentContent(doc: RenderableDocument): ReactNode[] {
    return doc.root.map((entry) => renderNode(doc, entry.id, entry.id));
}
